import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Truck,
  UserCheck,
  Building,
  Building2,
  CreditCard,
  BarChart3,
  Settings,
  CalendarDays,
  DollarSign,
  PanelLeftClose,
  PanelRightOpen,
} from 'lucide-react';
import { cn } from '../lib/cn';
import { appLogout } from '../lib/logout';
import { useNotifications } from './Notifications';
import { LogOut } from 'lucide-react';

const Sidebar = ({ currentUser, collapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { push } = useNotifications ? useNotifications() : { push: () => {} };
  
  // Check if current user is admin
  const isAdmin = currentUser?.role === 'admin';
  
  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Panel de Control' },
    { path: '/transactions', icon: CreditCard, label: 'Operaciones' },
    { path: '/clients', icon: Users, label: 'Clientes' },
    { path: '/suppliers', icon: Truck, label: 'Proveedores' },
    { path: '/brokers', icon: UserCheck, label: 'Brokers' },
    { path: '/branches', icon: Building, label: 'Sucursales' },
    // Businesses tab - admin only
    ...(isAdmin ? [{ path: '/businesses', icon: Building2, label: 'Empresas' }] : []),
    { path: '/purchase-management', icon: DollarSign, label: 'Gestión Compras' },
    { path: '/pending-cash-calendar', icon: CalendarDays, label: 'Efectivo Pendiente' },
    { path: '/profits', icon: BarChart3, label: 'Ganancias' },
    { path: '/reports', icon: BarChart3, label: 'Reportes' },
  { path: '/settings', icon: Settings, label: 'Configuración' },
  ];

  const roleLabel = !currentUser
    ? 'Usuario invitado'
    : currentUser.role === 'admin'
      ? 'Administrador Principal'
      : `Sucursal: ${currentUser.branchId ?? 'N/A'}`;

  const displayName = currentUser?.name || currentUser?.email || 'Usuario';
  const initials = (displayName || '')
    .split(' ')
    .map((s) => s.trim().charAt(0).toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join('') || 'U';

  return (
    <div
      className={cn(
        'flex h-full flex-col bg-gray-100 text-gray-900 transition-all duration-300 ease-in-out dark:bg-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-800 neon-border',
        collapsed ? 'w-20' : 'w-64'
      )}
    >
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className={cn('flex items-center', collapsed ? 'justify-center' : 'justify-between gap-3')}>
          <div className={cn('flex-1', collapsed && 'hidden')}>
            <span className="block text-xl font-bold text-gray-900 dark:text-gray-100">MONIPAY</span>
            <span className="mt-0.5 block text-sm font-medium uppercase tracking-wide text-blue-600 dark:text-blue-400">Operaciones en Efectivo</span>
          </div>
          {/* Removed top icon when collapsed */}
          {typeof onToggleCollapse === 'function' && (
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
              className="inline-flex items-center justify-center rounded-lg border border-transparent bg-white/60 p-2 text-gray-600 transition hover:bg-white hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 dark:bg-gray-800/60 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {collapsed ? <PanelRightOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
            </button>
          )}
        </div>
      </div>
      <div className={cn('border-b border-gray-200 px-4 py-3 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400', collapsed && 'hidden')}>
        {roleLabel}
      </div>
      <nav className={cn('flex-1 p-2', collapsed && 'space-y-1')}
        aria-label="Navegación principal"
      >
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'group flex items-center rounded-lg p-3 transition-colors',
                collapsed ? 'justify-center' : 'gap-3',
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800'
              )}
            >
              <Icon className={cn('h-5 w-5', collapsed ? '' : 'flex-shrink-0')} />
              {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
              {collapsed && <span className="sr-only">{item.label}</span>}
            </Link>
          );
        })}
      </nav>
      <div className="px-3 pt-2">
        <div className={cn('flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-gray-800/70 p-2', collapsed && 'justify-center')}
          aria-label="Información de usuario"
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-white flex items-center justify-center text-xs font-semibold">
            {initials}
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{displayName}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{currentUser?.email || ''}</div>
            </div>
          )}
        </div>
      </div>
      <div className="p-3 border-t border-gray-200 dark:border-gray-800">
        <button
          type="button"
          onClick={async () => {
            try {
              await appLogout('/login', { navigate, replace: true });
              push('Sesión cerrada', { type: 'success' });
            } catch (e) {
              try { push('No se pudo cerrar sesión. Intenta de nuevo.', { type: 'error' }); } catch {}
            }
          }}
          className={cn(
            'w-full inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm',
            'border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800',
            'text-gray-700 dark:text-gray-200 hover:bg-gray-100 hover:dark:bg-gray-700'
          )}
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && <span>Cerrar sesión</span>}
          {collapsed && <span className="sr-only">Cerrar sesión</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
